import { Link } from "gatsby";
import { IGatsbyImageData } from "gatsby-plugin-image";
import styled from "styled-components";
import tw from "twin.macro";

import FeatureImage from "./FeatureImage";
import { CategoryTag } from "./styles";

export interface IndexPostProps {
  title: string;
  url: string;
  gatsbyImageData?: IGatsbyImageData;
  fallbackImageSrc?: string;
  category: string;
  datePublished: string;
  dateUpdated: string;
  index?: number;
}

export const fromIndexPostContentfulBlogPostFragment = (
  post: Queries.IndexPostContentfulBlogPostFragmentFragment
): IndexPostProps => {
  const { title, slug, category, datePublished, dateUpdated } = post;

  return {
    title: title || "",
    url: `/blog/${slug}/`,
    gatsbyImageData: post?.featureImage?.gatsbyImageData || undefined,
    category: category || "",
    datePublished: datePublished || "",
    dateUpdated: dateUpdated || "",
  };
};

const Wrapper = styled.div<{ index?: number }>`
  ${tw`flex flex-col items-start`}
  gap: 30px;

  ${(props) =>
    props.index === 0 &&
    `
    grid-column: 1 / span 2;

    .blog-post-title-link {
      font-size: 26px;
      line-height: 34px;
    }
  `}

  ${(props) =>
    props.index === 1 &&
    `
    .blog-post-feature-image-positioner {
      &::before {
        display: block;
        content: "";
        aspect-ratio: calc(707 / 408);
        width: 100%;
      }
    }
  `}

  @media (max-width: 850px) {
    grid-column: unset;

    .blog-post-feature-image-positioner::before {
        display: none;
      }
    }
  }
`;

const Details = styled.div`
  ${tw`flex flex-col items-start flex-grow`}
  gap: 17px;
`;

const FeaturedImagePositioner = styled.div`
  ${tw`flex flex-col w-full`}
  gap: 22.5px;
`;

const FeaturedImageLink = styled(Link)`
  ${tw`block w-full`}
`;

const TitleLink = styled(Link)`
  ${tw`
    font-title text-title text-[21px] leading-[28px] font-medium
    hover:text-black hover:no-underline
    flex-grow
    relative
  `}
  top: 2px;
`;

const DatePublished = styled.div`
  ${tw`text-theme-gray-7 text-[16px]`}
`;

const IndexPost: React.FunctionComponent<IndexPostProps> = ({
  title,
  url,
  gatsbyImageData,
  fallbackImageSrc,
  category,
  datePublished,
  dateUpdated,
  index,
}) => (
  <Wrapper index={index}>
    <FeaturedImagePositioner className="blog-post-feature-image-positioner">
      <FeaturedImageLink to={url}>
        <FeatureImage
          gatsbyImageData={gatsbyImageData}
          fallbackImageSrc={fallbackImageSrc}
        />
      </FeaturedImageLink>
    </FeaturedImagePositioner>

    <Details>
      <CategoryTag>{category}</CategoryTag>

      <TitleLink to={url} className="blog-post-title-link">
        {title}
      </TitleLink>

      <DatePublished>
        {dateUpdated ? `Updated ${dateUpdated}` : datePublished}
      </DatePublished>
    </Details>
  </Wrapper>
);

export default IndexPost;
